import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Search,
  Loader2,
  AlertCircle,
  Plus,
  Save,
  Trash2,
  GraduationCap,
  History,
  Users,
  RotateCcw,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useToast, useConfirm } from '../../Components/Feedback';
import { Card, Input, Select, Button, Badge } from '../../Components/ui';
import { corCardNivel } from '../../utils/niveis';
import { formatarDataBR } from '../../utils/data';
import { API_URL } from '../../api/config';

const STATUS_MATRICULA = [
  { value: 'matriculado', label: 'Matriculado' },
  { value: 'cancelada', label: 'Cancelada' },
];
const STATUS_ALUNO = [
  { value: 'ativo', label: 'Ativo' },
  { value: 'inativo', label: 'Inativo' },
];

const CAMPOS_ALUNO = [
  { label: 'Nome', campo: 'nome', type: 'text' },
  { label: 'Data de Nascimento', campo: 'data_nascimento', type: 'date' },
  { label: 'Sexo', campo: 'sexo', type: 'text' },
  { label: 'Telefone', campo: 'telefone', type: 'text' },
  { label: 'Turma', campo: 'turma', type: 'text' },
  { label: 'Turno', campo: 'turno', type: 'text' },
  { label: 'Transporte', campo: 'transporte', type: 'text' },
  { label: 'Informações', campo: 'Inf', type: 'text' },
];

const formatarDataISO = (data) => {
  if (!data) return '';
  const d = new Date(data);
  return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
};

const rotuloTurma = (t) => {
  if (!t.dia_semana || !t.horario) return `${t.nome} (sem horário definido)`;
  const encerrada = t.data_fim ? ' [encerrada]' : '';
  return `${t.nome} — ${t.dia_semana} ${t.horario} (${t.turno})${encerrada}`;
};

// Uma matrícula com data_fim já virou histórico — o backend recusa qualquer
// PUT nela (ver historico-aluno.js), então a tela nem deixa tentar editar:
// só mostra os dados e oferece excluir (com muito cuidado, ver
// excluirMatriculaHistorica). Item novo (ainda não salvo) nunca é histórico.
const ehHistorica = (m) => !m.isNew && !!m.data_fim;

// Rótulo + cor de cada status de presença — usado na tabela de histórico de
// chamada pra falta e justificativa ficarem tão visíveis quanto presença, em
// vez de todo mundo aparecer igual em texto cinza (era isso que fazia parecer
// que só presença aparecia na lista — os dados sempre estiveram lá).
const STATUS_PRESENCA = {
  presente: { label: 'Presente', tone: 'success' },
  ausente: { label: 'Ausente', tone: 'danger' },
  justificado: { label: 'Justificado', tone: 'warning' },
};

const calcularEstatisticas = (presencas = []) => ({
  total: presencas.length,
  presentes: presencas.filter((p) => p.status === 'presente').length,
  ausentes: presencas.filter((p) => p.status === 'ausente').length,
  justificados: presencas.filter((p) => p.status === 'justificado').length,
});

// Ficha completa de um aluno (só para master): busca por nome, edição de dados
// cadastrais, matrículas e contatos de emergência, e histórico de presença —
// tudo numa tela só, salvo de uma vez pelo botão "Salvar Alterações". As rotas
// /api/historico-aluno/* que essa tela usa vivem em backend/historico-aluno.js.
//
// O dia/horário/turno de cada matrícula vem sempre da turma selecionada (ver
// GET /api/atividades) e não é mais editável direto aqui — o backend já
// recusa qualquer valor desses 3 campos vindo do cliente e recalcula a partir
// da turma, pra nunca deixar uma matrícula com horário divergente da turma
// que ela aponta (ver migrate-split-atividades-por-horario.js).
export default function HistoricoAlunoMaster() {
  const { token, user } = useAuth();
  const { institutionId, institutionName } = useInstitution();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();

  // Data de "hoje" do SERVIDOR (GET /api/hoje) — usada como padrão de
  // data_inicio ao adicionar uma matrícula nova (ver adicionarMatricula
  // abaixo); não pode depender do relógio de quem está editando o histórico.
  const [hojeServidor, setHojeServidor] = useState(null);
  useEffect(() => {
    fetch(`${API_URL}/api/hoje`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.hoje) setHojeServidor(data.hoje);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState([]);
  const [carregandoBusca, setCarregandoBusca] = useState(false);
  const [alunoSelecionado, setAlunoSelecionado] = useState(null);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [turmas, setTurmas] = useState([]);

  useEffect(() => {
    if (!user || (user.perfil !== 'master' && user.perfil !== 'coordenador')) {
      navigate('/');
    }
  }, [user, navigate]);

  // Memoizado por token: sem isso, uma nova instância a cada render invalida a
  // dependência de buscarAlunos (useCallback) a cada render, o que reinicia o
  // debounce da busca sem necessidade.
  const api = useMemo(
    () =>
      axios.create({
        baseURL: API_URL,
        headers: { Authorization: `Bearer ${token}` },
      }),
    [token],
  );

  const buscarAlunos = useCallback(
    async (termo) => {
      setCarregandoBusca(true);
      setErro('');
      try {
        const params = { q: termo };
        if (institutionId) params.id_instituicao = institutionId;
        const res = await api.get('/api/historico-aluno/buscar', { params });
        const list = res.data?.data ?? (Array.isArray(res.data) ? res.data : []);
        setResultados(Array.isArray(list) ? list : []);
      } catch (err) {
        setErro('Erro ao buscar inscritos: ' + (err.response?.data?.error || err.message));
      } finally {
        setCarregandoBusca(false);
      }
    },
    [api, institutionId],
  );

  // Busca com debounce: espera 400ms sem digitação antes de consultar a API,
  // pra não disparar uma requisição a cada tecla.
  useEffect(() => {
    const delay = setTimeout(() => {
      if (busca.trim().length >= 2) {
        buscarAlunos(busca);
      } else {
        setResultados([]);
      }
    }, 400);
    return () => clearTimeout(delay);
  }, [busca, buscarAlunos]);

  const carregarAluno = useCallback(
    async (id, instId) => {
      setCarregando(true);
      setErro('');
      try {
        const [alunoRes, turmasRes] = await Promise.all([
          api.get(`/api/historico-aluno/${id}`),
          api.get('/api/atividades', { headers: { 'x-institution-id': instId } }),
        ]);
        setDados(alunoRes.data?.data ?? alunoRes.data);
        const turmasList = turmasRes.data?.data ?? (Array.isArray(turmasRes.data) ? turmasRes.data : []);
        setTurmas(Array.isArray(turmasList) ? turmasList : []);
      } catch (err) {
        setErro('Erro ao carregar inscrito: ' + (err.response?.data?.error || err.message));
      } finally {
        setCarregando(false);
      }
    },
    [api],
  );

  const selecionarAluno = (aluno) => {
    setAlunoSelecionado(aluno);
    setBusca(aluno.nome);
    setResultados([]);
    const instId = aluno.id_instituicao || aluno.instituicao_id || aluno.idinstituicao;
    if (instId) {
      carregarAluno(aluno.id, instId);
    } else {
      setErro('Instituição do inscrito não identificada.');
    }
  };

  const atualizarCampoAluno = (campo, valor) => {
    setDados((prev) => ({ ...prev, aluno: { ...prev.aluno, [campo]: valor } }));
  };

  const atualizarMatricula = (id, campo, valor) => {
    setDados((prev) => ({
      ...prev,
      matriculas: prev.matriculas.map((m) => (m.id === id ? { ...m, [campo]: valor } : m)),
    }));
  };

  // Trocar a turma de uma matrícula já atualiza dia/horário/turno na hora, pra
  // pré-visualizar o que vai ser salvo — o backend recalcula isso de novo por
  // conta própria, então mesmo se algo aqui ficar dessincronizado o dado final
  // continua correto.
  const selecionarTurmaDaMatricula = (id, idatividades) => {
    const turma = turmas.find((t) => String(t.id) === String(idatividades));
    setDados((prev) => ({
      ...prev,
      matriculas: prev.matriculas.map((m) =>
        m.id === id
          ? {
              ...m,
              idatividades: idatividades ? Number(idatividades) : '',
              nome_atividade: turma?.nome || '',
              dia_semana: turma?.dia_semana || '',
              horario: turma?.horario || '',
              turno: turma?.turno || '',
            }
          : m,
      ),
    }));
  };

  const atualizarContato = (id, campo, valor) => {
    setDados((prev) => ({
      ...prev,
      contatos: prev.contatos.map((c) => (c.id === id ? { ...c, [campo]: valor } : c)),
    }));
  };

  const adicionarMatricula = () => {
    const nova = {
      id: `temp-${Date.now()}`,
      idaluno: alunoSelecionado.id,
      idatividades: '',
      nome_atividade: '',
      turno: '',
      horario: '',
      dia_semana: '',
      status: 'matriculado',
      data_inicio:
        hojeServidor ||
        (() => {
          const d = new Date();
          return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0];
        })(),
      data_fim: '',
      id_instituicao: alunoSelecionado.id_instituicao,
      isNew: true,
    };
    setDados((prev) => ({ ...prev, matriculas: [nova, ...prev.matriculas] }));
  };

  const adicionarContato = () => {
    const novo = {
      id: `temp-${Date.now()}`,
      id_aluno: alunoSelecionado.id,
      nome: '',
      telefone: '',
      parentesco: '',
      isNew: true,
    };
    setDados((prev) => ({ ...prev, contatos: [...prev.contatos, novo] }));
  };

  const removerItem = (campo, id) => {
    setDados((prev) => ({ ...prev, [campo]: prev[campo].filter((item) => item.id !== id) }));
  };

  // Salva tudo de uma vez: dados do aluno primeiro (1 PUT), depois cada
  // matrícula e cada contato em paralelo (são independentes entre si — sem
  // necessidade de esperar um pro outro). Itens novos (id temporário "temp-...",
  // ver adicionarMatricula/adicionarContato) viram POST; os existentes viram PUT.
  const salvar = async () => {
    const semTurma = dados.matriculas.some((m) => !m.idatividades);
    if (semTurma) {
      toast.warning('Selecione uma turma para cada matrícula antes de salvar.');
      return;
    }

    setSalvando(true);
    setErro('');
    try {
      const { aluno, matriculas, contatos } = dados;

      await api.put(`/api/historico-aluno/${aluno.id}`, {
        nome: aluno.nome,
        data_nascimento: aluno.data_nascimento,
        sexo: aluno.sexo,
        telefone: aluno.telefone,
        turma: aluno.turma,
        turno: aluno.turno,
        transporte: aluno.transporte,
        Inf: aluno.Inf,
        status: aluno.status,
      });

      await Promise.all([
        ...matriculas.map((m) => {
          const payload = {
            idatividades: m.idatividades,
            status: m.status,
            data_inicio: formatarDataISO(m.data_inicio) || null,
            data_fim: formatarDataISO(m.data_fim) || null,
          };
          return m.isNew
            ? api.post('/api/historico-aluno/matricula', {
                ...payload,
                idaluno: aluno.id,
                id_instituicao: aluno.id_instituicao,
              })
            : api.put(`/api/historico-aluno/matricula/${m.id}`, payload);
        }),
        ...contatos.map((c) => {
          const payload = { nome: c.nome, telefone: c.telefone, parentesco: c.parentesco };
          return c.isNew
            ? api.post('/api/historico-aluno/contato', {
                ...payload,
                id_aluno: aluno.id,
                id_instituicao: aluno.id_instituicao,
              })
            : api.put(`/api/historico-aluno/contato/${c.id}`, payload);
        }),
      ]);

      toast.success('Alterações salvas com sucesso!');
      carregarAluno(aluno.id, aluno.id_instituicao); // recarrega pra obter ids reais dos itens criados
    } catch (err) {
      console.error('[HistoricoAlunoMaster] Erro ao salvar:', err.response?.data || err);
      toast.error('Erro ao salvar: ' + (err.response?.data?.error || err.message));
    } finally {
      setSalvando(false);
    }
  };

  // Matrícula ATIVA: soft-close (vira histórico, mas continua no banco).
  const encerrarMatricula = async (id) => {
    if (
      !(await confirm('Encerrar esta matrícula?', {
        title: 'Encerrar matrícula',
        confirmText: 'Encerrar',
      }))
    )
      return;
    try {
      await api.delete(`/api/historico-aluno/matricula/${id}`);
      toast.success('Matrícula encerrada.');
      carregarAluno(alunoSelecionado.id, alunoSelecionado.id_instituicao);
    } catch (err) {
      toast.error('Erro ao encerrar matrícula: ' + (err.response?.data?.error || err.message));
    }
  };

  // Matrícula JÁ HISTÓRICA: o backend detecta o estado e faz hard-delete
  // (remove a linha de vez — ver DELETE /matricula/:id em historico-aluno.js).
  // Não tem como editar um registro histórico (o PUT recusa), então apagar é a
  // única correção possível — por isso a confirmação é bem mais enfática aqui.
  const excluirMatriculaHistorica = async (m) => {
    const ok = await confirm(
      `Isso vai apagar PERMANENTEMENTE o registro de "${m.nome_atividade || 'matrícula'}" (${formatarDataBR(m.data_inicio)} até ${formatarDataBR(m.data_fim)}) do histórico deste inscrito. Essa ação não pode ser desfeita.`,
      { title: 'Excluir registro do histórico', confirmText: 'Excluir permanentemente' },
    );
    if (!ok) return;
    try {
      await api.delete(`/api/historico-aluno/matricula/${m.id}`);
      toast.success('Registro excluído do histórico.');
      carregarAluno(alunoSelecionado.id, alunoSelecionado.id_instituicao);
    } catch (err) {
      toast.error('Erro ao excluir registro: ' + (err.response?.data?.error || err.message));
    }
  };

  // Matrícula JÁ HISTÓRICA: reabre (limpa data_fim, volta status pra
  // 'matriculado') — contraparte de encerrarMatricula. O backend recusa se a
  // turma estiver encerrada ou se já existir outra matrícula ativa do aluno
  // no mesmo dia/horário (ver POST /matricula/:id/reabrir).
  const reabrirMatricula = async (m) => {
    const ok = await confirm(
      `Reabrir a matrícula em "${m.nome_atividade || 'turma'}" (${m.dia_semana} · ${m.horario})? Ela volta a valer a partir de hoje.`,
      { title: 'Reabrir matrícula', confirmText: 'Reabrir' },
    );
    if (!ok) return;
    try {
      await api.post(`/api/historico-aluno/matricula/${m.id}/reabrir`);
      toast.success('Matrícula reaberta.');
      carregarAluno(alunoSelecionado.id, alunoSelecionado.id_instituicao);
    } catch (err) {
      toast.error('Erro ao reabrir matrícula: ' + (err.response?.data?.error || err.message));
    }
  };

  const excluirContato = async (id) => {
    if (
      !(await confirm('Excluir este contato?', {
        title: 'Excluir contato',
        confirmText: 'Excluir',
      }))
    )
      return;
    if (String(id).startsWith('temp')) {
      removerItem('contatos', id);
      return;
    }
    try {
      await api.delete(`/api/historico-aluno/contato/${id}`);
      toast.success('Contato excluído.');
      carregarAluno(alunoSelecionado.id, alunoSelecionado.id_instituicao);
    } catch (err) {
      toast.error('Erro ao excluir contato: ' + (err.response?.data?.error || err.message));
    }
  };

  const stats = dados ? calcularEstatisticas(dados.presencas) : null;

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-6xl mx-auto space-y-6">
        {erro && (
          <div className="p-2 rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 text-red-700 dark:text-red-400 text-sm flex items-start gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {erro}
          </div>
        )}

        <div className="relative">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
            Buscar aluno por nome{' '}
            {institutionName && (
              <span className="text-gray-400 dark:text-gray-500 font-normal">
                — restrito a {institutionName}
              </span>
            )}
          </label>
          <Input
            icon={Search}
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Digite pelo menos 2 caracteres..."
            size="lg"
          />
          {carregandoBusca && (
            <div className="mt-2 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Buscando...
            </div>
          )}
          {resultados.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl shadow-lg max-h-72 overflow-auto z-20">
              {resultados.map((a) => {
                const corNivel = corCardNivel(a.nivel);
                return (
                  <button
                    key={a.id}
                    onClick={() => selecionarAluno(a)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-50 dark:border-gray-800 last:border-0 transition-colors ${corNivel ? `${corNivel} hover:brightness-95 dark:hover:brightness-110` : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                  >
                    <div
                      className="text-sm font-medium text-gray-900 dark:text-gray-100"
                      translate="no"
                    >
                      {a.nome}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {a.nome_instituicao}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {carregando && (
          <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-3" />
            Carregando dados...
          </div>
        )}

        {dados && (
          <div className="space-y-6">
            <Card>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-green-50 dark:bg-green-900/30 flex items-center justify-center">
                  <History className="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Resumo de Frequência
                </h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                    {stats.total}
                  </div>
                  <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Total de Aulas
                  </div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                    {stats.presentes}
                  </div>
                  <div className="text-xs text-green-600 dark:text-green-400 mt-1">Presenças</div>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-red-700 dark:text-red-400">
                    {stats.ausentes}
                  </div>
                  <div className="text-xs text-red-600 dark:text-red-400 mt-1">Faltas</div>
                </div>
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-amber-700 dark:text-amber-400">
                    {stats.justificados}
                  </div>
                  <div className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    Justificativas
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                  <GraduationCap className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Dados do Inscrito
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {CAMPOS_ALUNO.map((item) => (
                  <Input
                    key={item.campo}
                    label={item.label}
                    type={item.type}
                    value={dados.aluno[item.campo] || ''}
                    onChange={(e) => atualizarCampoAluno(item.campo, e.target.value)}
                  />
                ))}
                <Select
                  label="Status"
                  value={dados.aluno.status || 'ativo'}
                  onChange={(e) => atualizarCampoAluno('status', e.target.value)}
                  options={STATUS_ALUNO}
                />
              </div>
            </Card>

            <Card padding="none" className="overflow-hidden">
              <div className="flex items-center justify-between p-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center">
                    <GraduationCap className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Histórico de Matrículas
                  </h3>
                </div>
                <Button variant="primary" icon={Plus} onClick={adicionarMatricula}>
                  Adicionar
                </Button>
              </div>
              {dados.matriculas.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 px-3 pb-3">
                  Nenhuma matrícula encontrada.
                </p>
              ) : (
                <div className="overflow-x-auto border-t border-gray-100 dark:border-gray-800">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800/60">
                      <tr>
                        <th className="px-3 py-2.5 text-left font-medium text-gray-500 dark:text-gray-400 text-xs w-64">
                          Turma
                        </th>
                        <th className="px-3 py-2.5 text-left font-medium text-gray-500 dark:text-gray-400 text-xs">
                          Dia / Horário / Turno
                        </th>
                        <th className="px-3 py-2.5 text-left font-medium text-gray-500 dark:text-gray-400 text-xs w-36">
                          Status
                        </th>
                        <th className="px-3 py-2.5 text-left font-medium text-gray-500 dark:text-gray-400 text-xs w-36">
                          Data Início
                        </th>
                        <th className="px-3 py-2.5 text-left font-medium text-gray-500 dark:text-gray-400 text-xs w-36">
                          Data Fim
                        </th>
                        <th className="px-3 py-2.5 text-right font-medium text-gray-500 dark:text-gray-400 text-xs">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {/* A Turma nunca é editável numa matrícula que já existe (ativa ou
                          histórica) — só em uma nova, ainda não salva. Trocar a turma de uma
                          matrícula existente reescreveria por cima de qual turma o aluno
                          esteve de fato; a forma correta de mudar é encerrar essa e criar
                          outra (ver botão "Adicionar" acima e "Encerrar Matrícula" abaixo). */}
                      {dados.matriculas.map((m) => {
                        const historica = ehHistorica(m);
                        return (
                          <tr key={m.id} className={historica ? 'opacity-60' : ''}>
                            <td className="px-3 py-2 align-top">
                              {m.isNew ? (
                                <Select
                                  value={m.idatividades || ''}
                                  onChange={(e) => selecionarTurmaDaMatricula(m.id, e.target.value)}
                                  options={[
                                    { value: '', label: 'Selecione...' },
                                    ...turmas.map((t) => ({ value: t.id, label: rotuloTurma(t) })),
                                  ]}
                                />
                              ) : (
                                <span className="text-gray-900 dark:text-gray-100" translate="no">
                                  {m.nome_atividade || '-'}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 align-top text-gray-600 dark:text-gray-400">
                              {m.dia_semana
                                ? `${m.dia_semana} · ${m.horario} · ${m.turno}`
                                : 'Definido pela turma'}
                            </td>
                            <td className="px-3 py-2 align-top">
                              {historica ? (
                                <Badge tone="neutral">Cancelada</Badge>
                              ) : (
                                <Select
                                  value={m.status || 'matriculado'}
                                  onChange={(e) =>
                                    atualizarMatricula(m.id, 'status', e.target.value)
                                  }
                                  options={STATUS_MATRICULA}
                                />
                              )}
                            </td>
                            <td className="px-3 py-2 align-top">
                              {historica ? (
                                <span className="text-gray-600 dark:text-gray-400">
                                  {formatarDataBR(m.data_inicio)}
                                </span>
                              ) : (
                                <Input
                                  type="date"
                                  value={formatarDataISO(m.data_inicio)}
                                  onChange={(e) =>
                                    atualizarMatricula(m.id, 'data_inicio', e.target.value)
                                  }
                                />
                              )}
                            </td>
                            <td className="px-3 py-2 align-top">
                              {historica ? (
                                <span className="text-gray-600 dark:text-gray-400">
                                  {formatarDataBR(m.data_fim)}
                                </span>
                              ) : (
                                <Input
                                  type="date"
                                  value={formatarDataISO(m.data_fim)}
                                  onChange={(e) =>
                                    atualizarMatricula(m.id, 'data_fim', e.target.value)
                                  }
                                />
                              )}
                            </td>
                            <td className="px-3 py-2 align-top">
                              <div className="flex justify-end gap-1">
                                {historica ? (
                                  <>
                                    <button
                                      onClick={() => reabrirMatricula(m)}
                                      title="Reabrir matrícula"
                                      className="w-8 h-8 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/30 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400 transition-colors flex-shrink-0"
                                    >
                                      <RotateCcw className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => excluirMatriculaHistorica(m)}
                                      title="Excluir permanentemente"
                                      className="w-8 h-8 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors flex-shrink-0"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </>
                                ) : (
                                  !m.isNew && (
                                    <button
                                      onClick={() => encerrarMatricula(m.id)}
                                      title="Encerrar matrícula"
                                      className="w-8 h-8 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/30 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-amber-600 dark:hover:text-amber-400 transition-colors flex-shrink-0"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <Card>
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center">
                    <Users className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Contatos de Emergência
                  </h3>
                </div>
                <Button variant="primary" icon={Plus} onClick={adicionarContato}>
                  Adicionar
                </Button>
              </div>
              {dados.contatos.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Nenhum contato cadastrado.
                </p>
              ) : (
                <div className="space-y-3">
                  {dados.contatos.map((c) => (
                    <div
                      key={c.id}
                      className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end p-2 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50/30 dark:bg-gray-800/30"
                    >
                      <Input
                        label="Nome"
                        value={c.nome || ''}
                        onChange={(e) => atualizarContato(c.id, 'nome', e.target.value)}
                      />
                      <Input
                        label="Telefone"
                        value={c.telefone || ''}
                        onChange={(e) => atualizarContato(c.id, 'telefone', e.target.value)}
                      />
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <Input
                            label="Parentesco"
                            value={c.parentesco || ''}
                            onChange={(e) => atualizarContato(c.id, 'parentesco', e.target.value)}
                          />
                        </div>
                        <button
                          onClick={() => excluirContato(c.id)}
                          className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 flex items-center justify-center transition-colors flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card padding="none" className="overflow-hidden">
              <div className="flex items-center gap-2 p-3">
                <div className="w-8 h-8 rounded-lg bg-green-50 dark:bg-green-900/30 flex items-center justify-center">
                  <History className="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Histórico de Chamada
                </h3>
              </div>
              {dados.presencas.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 px-3 pb-3">
                  Nenhum registro de presença.
                </p>
              ) : (
                <div className="max-h-96 overflow-auto border-t border-gray-100 dark:border-gray-800">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-800">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400">
                          Data
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400">
                          Status
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400">
                          Observação
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.presencas.map((p) => {
                        const statusInfo = STATUS_PRESENCA[p.status] || {
                          label: p.status || '-',
                          tone: 'neutral',
                        };
                        return (
                          <tr
                            key={p.id}
                            className="border-b border-gray-50 dark:border-gray-800 last:border-0"
                          >
                            <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                              {formatarDataBR(p.data)}
                            </td>
                            <td className="px-4 py-3">
                              <Badge tone={statusInfo.tone}>{statusInfo.label}</Badge>
                            </td>
                            <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                              {p.observacao || '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => navigate('/')}>
                Cancelar
              </Button>
              <Button variant="primary" icon={Save} disabled={salvando} onClick={salvar}>
                {salvando ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
